import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AutoGraphRoundedIcon from "@mui/icons-material/AutoGraphRounded";
import CodeRoundedIcon from "@mui/icons-material/CodeRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import { ClientProps } from "../../interfaces/interfaces";
import styles from "../components.module.sass";
import { TitleComponent } from "../ui/TitleComponent";

export const SUGGESTED_REPORT_TYPES = ["Performance", "Refurbishment"];
export type ReportSourceType = "excel" | "html";

export interface ReportFormValues {
  client: ClientProps;
  reportType: string;
  sourceType: ReportSourceType;
  title: string;
  period: string;
  file?: File | null;
}

interface Props {
  clientOptions: ClientProps[];
  initialValues?: Partial<ReportFormValues>;
  submitting?: boolean;
  error?: string | null;
  onCancel: () => void;
  onSubmit: (values: ReportFormValues) => void | Promise<void>;
}

const emptyClient = { id: 0, name: "" };
const acceptedFiles: Record<ReportSourceType, string> = {
  excel:
    ".xlsx,.xlsm,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel",
  html: ".html,.htm,text/html",
};

export const ReportForm = ({
  clientOptions,
  initialValues,
  submitting = false,
  error,
  onCancel,
  onSubmit,
}: Props) => {
  const [values, setValues] = useState<ReportFormValues>(() => ({
    client: initialValues?.client ?? emptyClient,
    reportType: initialValues?.reportType ?? "",
    sourceType: initialValues?.sourceType ?? "excel",
    title: initialValues?.title ?? "",
    period: initialValues?.period ?? "",
    file: null,
  }));

  const isHtml = values.sourceType === "html";
  const canSave = Boolean(
    values.client.id &&
      values.reportType &&
      values.file &&
      (!isHtml || (values.title.trim() && values.period.trim())),
  );

  const selectSourceType = (sourceType: ReportSourceType | null) => {
    if (!sourceType || sourceType === values.sourceType) return;
    setValues((current) => ({ ...current, sourceType, file: null }));
  };

  return (
    <div className={styles.customToolForm}>
      <TitleComponent title={initialValues ? "Replace report" : "Add report"} />
      <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
        Generate a dashboard from source data or upload a finished dashboard.
      </Typography>

      <ToggleButtonGroup
        aria-label="Report source"
        disabled={submitting}
        exclusive
        fullWidth
        onChange={(_, sourceType: ReportSourceType | null) => selectSourceType(sourceType)}
        sx={{
          mb: 2.5,
          gap: 1.25,
          "& .MuiToggleButtonGroup-grouped": {
            m: 0,
            border: "1px solid #DFDED7 !important",
            borderRadius: "12px !important",
          },
        }}
        value={values.sourceType}
      >
        <ToggleButton
          aria-label="Generate from Excel"
          value="excel"
          sx={{
            alignItems: "flex-start",
            color: "#59616D",
            justifyContent: "flex-start",
            p: 1.5,
            textAlign: "left",
            textTransform: "none",
            "&.Mui-selected": {
              bgcolor: "#FFF7EA",
              borderColor: "#ED8B00 !important",
              color: "#29313D",
            },
            "&.Mui-selected:hover": { bgcolor: "#FFF2DC" },
          }}
        >
          <AutoGraphRoundedIcon sx={{ color: "#ED8B00", mr: 1.1, mt: 0.2 }} />
          <Box>
            <Typography fontSize={13.5} fontWeight={850}>Generate from Excel</Typography>
            <Typography color="text.secondary" fontSize={11.5}>Build the dashboard automatically</Typography>
          </Box>
        </ToggleButton>
        <ToggleButton
          aria-label="Upload ready HTML"
          value="html"
          sx={{
            alignItems: "flex-start",
            color: "#59616D",
            justifyContent: "flex-start",
            p: 1.5,
            textAlign: "left",
            textTransform: "none",
            "&.Mui-selected": {
              bgcolor: "#F1F4F7",
              borderColor: "#596574 !important",
              color: "#29313D",
            },
            "&.Mui-selected:hover": { bgcolor: "#E8EDF1" },
          }}
        >
          <CodeRoundedIcon sx={{ color: "#596574", mr: 1.1, mt: 0.2 }} />
          <Box>
            <Typography fontSize={13.5} fontWeight={850}>Upload ready HTML</Typography>
            <Typography color="text.secondary" fontSize={11.5}>Save a finished dashboard directly</Typography>
          </Box>
        </ToggleButton>
      </ToggleButtonGroup>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Grid container spacing={2}>
        <Grid item lg={12} xs={12}>
          <TextField
            className={styles.formFieldModalCustom}
            disabled={submitting}
            label="Client"
            onChange={(event) => {
              const client = clientOptions.find(
                (option) => String(option.id) === event.target.value,
              ) ?? emptyClient;
              setValues((current) => ({ ...current, client }));
            }}
            select
            SelectProps={{ native: true }}
            value={values.client.id || ""}
          >
            <option value="" disabled>Select a client...</option>
            {clientOptions.map((client) => (
              <option key={client.id} value={client.id}>{client.name}</option>
            ))}
          </TextField>
        </Grid>
        <Grid item lg={12} xs={12}>
          <TextField
            className={styles.formFieldModalCustom}
            disabled={submitting}
            label="Report type"
            onChange={(event) => setValues((current) => ({
              ...current,
              reportType: event.target.value,
            }))}
            select
            SelectProps={{ native: true }}
            value={values.reportType}
          >
            <option value="" disabled>Select a report type...</option>
            {SUGGESTED_REPORT_TYPES.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </TextField>
        </Grid>

        {isHtml ? (
          <>
            <Grid item lg={12} xs={12}>
              <TextField
                className={styles.formFieldModalCustom}
                disabled={submitting}
                inputProps={{ maxLength: 255 }}
                label="Title"
                onChange={(event) => setValues((current) => ({
                  ...current,
                  title: event.target.value,
                }))}
                value={values.title}
              />
            </Grid>
            <Grid item lg={12} xs={12}>
              <TextField
                className={styles.formFieldModalCustom}
                disabled={submitting}
                inputProps={{ maxLength: 255 }}
                label="Period"
                onChange={(event) => setValues((current) => ({
                  ...current,
                  period: event.target.value,
                }))}
                placeholder="e.g. Q3 2026"
                value={values.period}
              />
            </Grid>
          </>
        ) : null}

        <Grid item lg={12} xs={12}>
          <Typography component="span" display="block" fontWeight={750} mb={0.75} variant="body2">
            {isHtml ? "HTML dashboard" : "Excel workbook"}
          </Typography>
          <Button
            component="label"
            disabled={submitting}
            fullWidth
            startIcon={<UploadFileRoundedIcon />}
            sx={{
              borderColor: values.file ? "#ED8B00" : "#D7D8D4",
              color: values.file ? "#8A5200" : "#59616D",
              justifyContent: "flex-start",
              minHeight: 44,
              textTransform: "none",
            }}
            variant="outlined"
          >
            {values.file?.name || (isHtml ? "Choose HTML file" : "Choose Excel file")}
            <input
              accept={acceptedFiles[values.sourceType]}
              aria-label={isHtml ? "HTML dashboard file" : "Excel workbook file"}
              hidden
              key={values.sourceType}
              name="file"
              onChange={(event) => setValues((current) => ({
                ...current,
                file: event.target.files?.[0] ?? null,
              }))}
              type="file"
            />
          </Button>
          <Typography color="text.secondary" display="block" mt={0.75} variant="caption">
            {isHtml
              ? "Use a self-contained UTF-8 file with embedded scripts, styles, and images."
              : "Performance expects “Test Report1”; Refurbishment expects “InputData”."}
          </Typography>
        </Grid>
      </Grid>

      <div className={styles.buttonGral}>
        <Button disabled={submitting} onClick={onCancel} variant="contained">Cancel</Button>
        <Button
          disabled={!canSave || submitting}
          onClick={() => onSubmit(values)}
          startIcon={submitting ? <CircularProgress color="inherit" size={16} /> : undefined}
          variant="contained"
        >
          {submitting
            ? "Saving..."
            : isHtml
              ? "Upload dashboard"
              : "Generate dashboard"}
        </Button>
      </div>
    </div>
  );
};
